// ============================================
// ChatSidebar Component
// Displays list of conversations and groups
// ============================================

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAnonSupportSidebarSync } from '@/hooks/useAnonSupportSidebarSync';
import { useNavigate } from 'react-router-dom';
import { useUserPresence } from '@/hooks/useChat';
import { useChatContext } from '@/context/ChatContext';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Mail,
  Users,
  Search,
  X,
  Plus,
  Image as ImageIcon,
  FileText,
  Video,
  Maximize2,
} from 'lucide-react';
import { cn, offcanvasSidebarPanelClass } from '@/lib/utils';
import { getAvatarInitials } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CreateGroupChatModal } from './CreateGroupChatModal';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useSession } from '@/context/SessionContext';
import { AnonymousSupportConversation, getDefaultSupportStoreId, listAnonymousSupportConversations } from '@/lib/anonymousSupportChat';
import { supabase } from '@/lib/supabase/client';

interface LatestMessagePreview {
  content: string | null;
  attachment_url: string | null;
  attachment_type: string | null;
  attachment_name: string | null;
  created_at?: string | null;
}

interface LatestAnonymousPreview {
  message: string | null;
  created_at: string | null;
  support_store_id?: string | null;
}

interface GroupStarterProfile {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
}

const getStoreIdFromGroupDescription = (description?: string | null): string | null => {
  if (!description) return null;
  const match = description.trim().match(/^store-group:([0-9a-fA-F-]{36})$/);
  return match?.[1] || null;
};

const getAnonymousSessionIdFromGroupDescription = (description?: string | null): string | null => {
  if (!description) return null;
  const match = description.trim().match(/^anonymous-support:([0-9a-fA-F-]{36})$/);
  return match?.[1] || null;
};

export const ChatSidebar: React.FC = () => {
  const navigate = useNavigate();
  const {
    sidebarOpen,
    closeSidebar,
    openChatWindow,
    closeWithDelay,
    cancelCloseDelay,
    conversations,
    groups,
    anonymousSessions,
    loadingConversations,
    conversationsError,
  } = useChatContext();
  const { session } = useSession();
  const [searchQuery, setSearchQuery] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [anonymousConversations, setAnonymousConversations] = useState<AnonymousSupportConversation[]>([]);
  const [latestDmPreviewById, setLatestDmPreviewById] = useState<Record<string, LatestMessagePreview>>({});
  const [latestGroupPreviewById, setLatestGroupPreviewById] = useState<Record<string, LatestMessagePreview>>({});
  const [latestAnonymousPreviewBySessionId, setLatestAnonymousPreviewBySessionId] = useState<Record<string, LatestAnonymousPreview>>({});
  const [storeAvatarById, setStoreAvatarById] = useState<Record<string, string>>({});
  const [groupClientNameById, setGroupClientNameById] = useState<Record<string, string>>({});
  const [groupStarterByGroupId, setGroupStarterByGroupId] = useState<Record<string, GroupStarterProfile>>({});
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();

  // Pull-to-close: ref al ScrollArea
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Get user IDs for presence tracking
  const userIds = useMemo(() => {
    const dmUserIds = conversations.map((c) => c.other_user_id).filter(Boolean);
    const starterUserIds = (groups || [])
      .map((group: any) => group?.starter_user_id)
      .filter(Boolean) as string[];
    return Array.from(new Set([...dmUserIds, ...starterUserIds]));
  }, [conversations, groups]);

  const { isOnline, refetch: refetchPresences } = useUserPresence(userIds);

  useEffect(() => {
    if (!sidebarOpen || userIds.length === 0) return;
    void refetchPresences();
  }, [sidebarOpen, userIds.join(','), refetchPresences]);

  useEffect(() => {
    if (session?.user?.id) {
      setAnonymousConversations([]);
      return;
    }

    const syncAnonymousConversations = () => {
      setAnonymousConversations(listAnonymousSupportConversations());
    };

    syncAnonymousConversations();

    window.addEventListener('storage', syncAnonymousConversations);
    window.addEventListener('chat:sync', syncAnonymousConversations);
    window.addEventListener('anonymous-support-conversations-updated', syncAnonymousConversations);

    const intervalId = setInterval(syncAnonymousConversations, 1500);

    return () => {
      window.removeEventListener('storage', syncAnonymousConversations);
      window.removeEventListener('chat:sync', syncAnonymousConversations);
      window.removeEventListener('anonymous-support-conversations-updated', syncAnonymousConversations);
      clearInterval(intervalId);
    };
  }, [session?.user?.id]);

  // Hook para mantener actualizado el pseudo-DM anónimo en la sidebar
  useAnonSupportSidebarSync(anonymousConversations[0]?.sessionId, anonymousConversations[0]?.storeId);

  // Listen for real-time changes to force refresh of previews and local badges
  useEffect(() => {
    if (!sidebarOpen) return;

    const messageChannel = supabase
      .channel(`chat_sidebar_monitor_${Date.now()}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        () => {
          console.debug('[ChatSidebar] Nuevo mensaje detectado, actualizando');
          setRefreshTrigger((prev) => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'group_chats' },
        () => {
          console.debug('[ChatSidebar] Cambio en grupo detectado');
          setRefreshTrigger((prev) => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'anonymous_support_chats' },
        () => {
          console.debug('[ChatSidebar] Nuevo mensaje anónimo detectado');
          setRefreshTrigger((prev) => prev + 1);
          if (!session?.user?.id) {
            setAnonymousConversations(listAnonymousSupportConversations());
          }
        }
      )
      .subscribe();

    return () => {
      messageChannel.unsubscribe();
    };
  }, [sidebarOpen, session?.user?.id]);

  // Filter conversations by search query (usar display name si existe)
  const filteredConversations = conversations.filter((conv) => {
    const name = ((conv.other_display_name || conv.other_username) || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const filteredGroups = groups.filter((group) =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const storeGroupStoreIds = useMemo(
    () => Array.from(
      new Set(
        (groups || []).map((group: any) => group?.support_store_id || getStoreIdFromGroupDescription(group?.description)).filter(Boolean)
      )
    ) as string[],
    [groups]
  );

  const groupStoreIdByGroupId = useMemo(() => {
    const map: Record<string, string> = {};
    (groups || []).forEach((group: any) => {
      const storeId = group?.support_store_id || getStoreIdFromGroupDescription(group?.description);
      if (group?.id && storeId) {
        map[group.id] = storeId;
      }
    });
    return map;
  }, [groups]);

  const getGroupDisplayName = useCallback((group: any) => {
    const baseName = String(group?.name || 'Grupo');
    const anonymousSessionId = getAnonymousSessionIdFromGroupDescription(group?.description);
    if (anonymousSessionId) {
      return `${baseName} · Anónimo`;
    }

    const clientName = groupClientNameById[group?.id];
    if (!clientName) return baseName;

    return baseName.replace(/\bEquipo\b/i, clientName);
  }, [groupClientNameById]);

  const anonymousSessionIdsKey = useMemo(() => {
    const sessionIds = Array.from(
      new Set(
        [
          ...(anonymousSessions || []).map((s: any) => s?.anonymous_session_id).filter(Boolean),
          ...anonymousConversations.map((s) => s?.sessionId).filter(Boolean),
        ]
      )
    ) as string[];

    return sessionIds.sort().join('|');
  }, [anonymousSessions, anonymousConversations]);

  useEffect(() => {
    let cancelled = false;

    const loadLatestPreviews = async () => {
      const dmIds = conversations.map((conv) => conv.id).filter(Boolean);
      const groupIds = groups.map((group) => group.id).filter(Boolean);

      if (dmIds.length === 0) {
        setLatestDmPreviewById({});
      }
      if (groupIds.length === 0) {
        setLatestGroupPreviewById({});
      }

      const [dmEntries, groupEntries] = await Promise.all([
        Promise.all(
          dmIds.map(async (conversationId) => {
            try {
              const { data, error } = await supabase
                .from('messages')
                .select('content,attachment_url,attachment_type,attachment_name,created_at,is_deleted')
                .eq('dm_conversation_id', conversationId)
                .or('is_deleted.is.null,is_deleted.eq.false')
                .order('created_at', { ascending: false })
                .order('id', { ascending: false })
                .limit(1);

              if (error) throw error;

              const latest = data?.[0];
              if (!latest) return [conversationId, null] as const;

              return [
                conversationId,
                {
                  content: latest.content || null,
                  attachment_url: latest.attachment_url || null,
                  attachment_type: latest.attachment_type || null,
                  attachment_name: latest.attachment_name || null,
                  created_at: latest.created_at || null,
                } satisfies LatestMessagePreview,
              ] as const;
            } catch (err) {
              return [conversationId, null] as const;
            }
          })
        ),
        Promise.all(
          groups.map(async (group) => {
            try {
              const { data, error } = await supabase.rpc('get_group_latest_message', {
                p_group_id: group.id,
              });

              if (error) throw error;

              const latest = Array.isArray(data) ? data[0] : null;
              if (latest) {
                return [
                  group.id,
                  {
                    content: latest.content || null,
                    attachment_url: latest.attachment_url || null,
                    attachment_type: latest.attachment_type || null,
                    attachment_name: latest.attachment_name || null,
                    created_at: latest.created_at || null,
                  } satisfies LatestMessagePreview,
                ] as const;
              }
            } catch {
              // Fallback below.
            }

            return [
              group.id,
              {
                content: group.last_message_content || null,
                attachment_url: null,
                attachment_type: null,
                attachment_name: null,
                created_at: group.last_message_at || null,
              } satisfies LatestMessagePreview,
            ] as const;
          })
        ),
      ]);

      if (cancelled) return;

      const nextDmPreviews: Record<string, LatestMessagePreview> = {};
      dmEntries.forEach(([conversationId, preview]) => {
        if (!preview) return;
        nextDmPreviews[conversationId] = preview;
      });

      const nextGroupPreviews: Record<string, LatestMessagePreview> = {};
      groupEntries.forEach(([groupId, preview]) => {
        if (!preview) return;
        nextGroupPreviews[groupId] = preview;
      });

      setLatestDmPreviewById(nextDmPreviews);
      setLatestGroupPreviewById(nextGroupPreviews);
    };

    void loadLatestPreviews();

    const intervalId = setInterval(() => {
      void loadLatestPreviews();
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [conversations, groups, refreshTrigger]);

  useEffect(() => {
    let cancelled = false;

    const fetchLatestAnonymousPreview = async (sessionId: string): Promise<LatestAnonymousPreview | null> => {
      // Primary path: direct table query (works for anonymous user flow).
      try {
        const { data, error } = await supabase
          .from('anonymous_support_chats')
          .select('message,created_at,support_store_id')
          .eq('anonymous_session_id', sessionId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!error && data?.[0]) {
          return {
            message: data[0].message || null,
            created_at: data[0].created_at || null,
            support_store_id: data[0].support_store_id || null,
          };
        }
        if (error) {
          // Silent failure, will fall through to RPC
        }
      } catch {
        // Fallback handled below.
      }

      // Fallback path: staff/security-definer RPC, ordered ASC in SQL -> pick the last row.
      try {
        const { data, error } = await supabase.rpc('get_anonymous_chat_messages', {
          p_anonymous_session_id: sessionId,
        });
        if (error) throw error;

        const rows = Array.isArray(data) ? data : [];
        const latest = rows.length > 0 ? rows[rows.length - 1] : null;
        if (!latest) return null;

        return {
          message: latest.message || null,
          created_at: latest.created_at || null,
          support_store_id: null,
        };
      } catch (err) {
        return null;
      }
    };

    const loadLatestAnonymousPreviews = async () => {
      const sessionIds = anonymousSessionIdsKey ? anonymousSessionIdsKey.split('|').filter(Boolean) : [];

      const nextMap: Record<string, LatestAnonymousPreview> = {};

      if (sessionIds.length > 0) {
        const entries = await Promise.all(
          sessionIds.map(async (sessionId) => {
            const latest = await fetchLatestAnonymousPreview(sessionId);
            if (!latest) return [sessionId, null] as const;

            return [sessionId, latest] as const;
          })
        );

        if (cancelled) return;

        entries.forEach(([sessionId, preview]) => {
          if (!preview) return;
          nextMap[sessionId] = preview;
        });
      }

      setLatestAnonymousPreviewBySessionId((prev) => {
        const prevKeys = Object.keys(prev).sort();
        const nextKeys = Object.keys(nextMap).sort();
        if (prevKeys.length !== nextKeys.length) return nextMap;

        for (let i = 0; i < nextKeys.length; i += 1) {
          const key = nextKeys[i];
          if (prevKeys[i] !== key) return nextMap;
          if (prev[key]?.created_at !== nextMap[key]?.created_at) return nextMap;
          if (prev[key]?.message !== nextMap[key]?.message) return nextMap;
        }

        return prev;
      });

      const candidateStoreIds = Array.from(
        new Set(
          [
            ...(anonymousSessions || []).map((s: any) => s?.support_store_id).filter(Boolean),
            ...anonymousConversations.map((s) => s?.storeId).filter(Boolean),
            ...Object.values(nextMap).map((p) => p?.support_store_id).filter(Boolean),
            ...storeGroupStoreIds,
            getDefaultSupportStoreId(),
          ]
        )
      ) as string[];

      if (candidateStoreIds.length === 0) {
        setStoreAvatarById({});
      } else {
        const { data: storesData, error: storesError } = await supabase
          .from('stores')
          .select('id,avatar_url')
          .in('id', candidateStoreIds);

        if (!storesError) {
          const nextAvatars: Record<string, string> = {};
          (storesData || []).forEach((store: any) => {
            if (store?.id && store?.avatar_url) {
              nextAvatars[store.id] = store.avatar_url;
            }
          });
          setStoreAvatarById(nextAvatars);
        }
      }
    };

    void loadLatestAnonymousPreviews();
    const intervalId = setInterval(() => {
      void loadLatestAnonymousPreviews();
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [anonymousSessionIdsKey, anonymousSessions, anonymousConversations, storeGroupStoreIds, refreshTrigger]);

  useEffect(() => {
    let cancelled = false;

    const loadGroupClientNames = async () => {
      const groupIds = (groups || []).map((g) => g.id).filter(Boolean);
      if (groupIds.length === 0) {
        setGroupClientNameById({});
        setGroupStarterByGroupId({});
        return;
      }

      const starterUserIds = Array.from(
        new Set(
          (groups || [])
            .map((group: any) => group?.starter_user_id)
            .filter(Boolean)
        )
      ) as string[];

      const { data: starterProfilesData, error: starterProfilesError } = starterUserIds.length
        ? await supabase
            .from('profiles')
            .select('id,display_name,username,avatar_url')
            .in('id', starterUserIds)
        : { data: [] as any[], error: null as any };

      if (starterProfilesError) {
        console.debug('[ChatSidebar][group-client-name][starter-profiles-error]', {
          starterProfilesError,
        });
      }

      const starterProfileByUserId = new Map(
        (starterProfilesData || []).map((profile: any) => [profile.id, profile])
      );

      const starterMapByGroupId: Record<string, GroupStarterProfile> = {};
      (groups || []).forEach((group: any) => {
        const groupId = group?.id;
        const starterUserId = group?.starter_user_id as string | undefined;
        if (!groupId || !starterUserId) return;

        const starterProfile = starterProfileByUserId.get(starterUserId);
        const starterDisplay = starterProfile?.display_name || starterProfile?.username || 'Usuario';

        starterMapByGroupId[groupId] = {
          userId: starterUserId,
          displayName: starterDisplay,
          avatarUrl: starterProfile?.avatar_url || null,
        };
      });

      if (cancelled) return;
      setGroupStarterByGroupId(starterMapByGroupId);

      const storeIds = Array.from(new Set(Object.values(groupStoreIdByGroupId).filter(Boolean)));
      if (storeIds.length === 0) {
        setGroupClientNameById({});
        return;
      }

      const resolvedByStarter: Record<string, string> = {};
      (groups || []).forEach((group: any) => {
        const groupId = group?.id;
        const baseName = String(group?.name || '');
        const anonymousSessionId = getAnonymousSessionIdFromGroupDescription(group?.description);
        const starterUserId = group?.starter_user_id as string | undefined;

        if (!groupId || !starterUserId || anonymousSessionId) return;
        if (!/\bEquipo\b/i.test(baseName)) return;
        if (starterUserId === session?.user?.id) return;

        const starterProfile = starterProfileByUserId.get(starterUserId);
        const starterDisplay = starterProfile?.display_name || starterProfile?.username || null;
        if (starterDisplay) {
          resolvedByStarter[groupId] = starterDisplay;
        }
      });

      const [{ data: membersData, error: membersError }, { data: storeMembersData, error: storeMembersError }] = await Promise.all([
        supabase
          .from('group_chat_members')
          .select('group_chat_id,user_id,joined_at')
          .in('group_chat_id', groupIds),
        supabase
          .from('store_members')
          .select('store_id,user_id')
          .in('store_id', storeIds),
      ]);

      if (membersError || storeMembersError) {
        console.debug('[ChatSidebar][group-client-name][query-error]', {
          membersError,
          storeMembersError,
        });
        return;
      }

      const memberUserIds = Array.from(
        new Set((membersData || []).map((row: any) => row?.user_id).filter(Boolean))
      );

      const { data: profilesData, error: profilesError } = memberUserIds.length
        ? await supabase
            .from('profiles')
            .select('id,display_name,username')
            .in('id', memberUserIds)
        : { data: [] as any[], error: null as any };

      if (profilesError) {
        console.debug('[ChatSidebar][group-client-name][profiles-error]', {
          profilesError,
        });
      }

      const profileByUserId = new Map(
        (profilesData || []).map((profile: any) => [profile.id, profile])
      );

      const storeUserIdsByStoreId = new Map<string, Set<string>>();
      (storeMembersData || []).forEach((row: any) => {
        if (!row?.store_id || !row?.user_id) return;
        if (!storeUserIdsByStoreId.has(row.store_id)) {
          storeUserIdsByStoreId.set(row.store_id, new Set());
        }
        storeUserIdsByStoreId.get(row.store_id)!.add(row.user_id);
      });

      const membersByGroupId = new Map<string, any[]>();
      (membersData || []).forEach((row: any) => {
        if (!row?.group_chat_id) return;
        if (!membersByGroupId.has(row.group_chat_id)) {
          membersByGroupId.set(row.group_chat_id, []);
        }
        membersByGroupId.get(row.group_chat_id)!.push({
          ...row,
          profiles: profileByUserId.get(row.user_id) || null,
        });
      });

      const nextMap: Record<string, string> = {};

      Object.assign(nextMap, resolvedByStarter);

      (groups || []).forEach((group: any) => {
        const groupId = group?.id;
        const baseName = String(group?.name || '');
        const storeId = groupStoreIdByGroupId[groupId];
        const anonymousSessionId = getAnonymousSessionIdFromGroupDescription(group?.description);

        if (!groupId || !storeId || anonymousSessionId) return;
        if (!/\bEquipo\b/i.test(baseName)) return;
        if (nextMap[groupId]) return;

        const storeUserIds = storeUserIdsByStoreId.get(storeId) || new Set<string>();
        const members = (membersByGroupId.get(groupId) || []).slice().sort((a, b) => {
          const aTs = a?.joined_at ? new Date(a.joined_at).getTime() : 0;
          const bTs = b?.joined_at ? new Date(b.joined_at).getTime() : 0;
          return aTs - bTs;
        });

        const externalMember = members.find((member: any) => member?.user_id && !storeUserIds.has(member.user_id));
        const nonSelfMember = members.find((member: any) => member?.user_id && member.user_id !== session?.user?.id);
        const selectedMember = externalMember || nonSelfMember;
        const displayName = selectedMember?.profiles?.display_name || selectedMember?.profiles?.username || null;

        console.debug('[ChatSidebar][group-client-name][debug]', {
          groupId,
          baseName,
          storeId,
          anonymousSessionId,
          currentUserId: session?.user?.id || null,
          storeUserCount: storeUserIds.size,
          memberUserIds: members.map((m: any) => m?.user_id).filter(Boolean),
          storeMemberInGroupUserIds: members
            .map((m: any) => m?.user_id)
            .filter((uid: any) => Boolean(uid) && storeUserIds.has(uid)),
          externalMemberUserId: externalMember?.user_id || null,
          fallbackNonSelfUserId: nonSelfMember?.user_id || null,
          resolvedDisplayName: displayName,
        });

        if (displayName) {
          nextMap[groupId] = displayName;
        }
      });

      if (cancelled) return;
      console.debug('[ChatSidebar][group-client-name][map]', nextMap);
      setGroupClientNameById(nextMap);
    };

    void loadGroupClientNames();

    return () => {
      cancelled = true;
    };
  }, [groups, groupStoreIdByGroupId, refreshTrigger, session?.user?.id]);

  const renderMessagePreview = (preview?: LatestMessagePreview | null) => {
    if (!preview) {
      return <p className="text-xs text-muted-foreground line-clamp-1">No hay mensajes</p>;
    }

    const content = (preview.content || '').trim();
    const attachmentUrl = preview.attachment_url || '';
    const attachmentType = preview.attachment_type || '';

    if (attachmentType.startsWith('image/') || (attachmentUrl.startsWith('http') && (attachmentUrl.includes('.jpg') || attachmentUrl.includes('.png') || attachmentUrl.includes('.gif') || attachmentUrl.includes('.webp')))) {
      return (
        <p className="text-xs text-muted-foreground line-clamp-1 flex items-center gap-1">
          <ImageIcon className="w-3 h-3 shrink-0" />
          <span>Imagen</span>
        </p>
      );
    }

    if (attachmentType.startsWith('video/') || (attachmentUrl.startsWith('http') && (attachmentUrl.includes('.mp4') || attachmentUrl.includes('.webm')))) {
      return (
        <p className="text-xs text-muted-foreground line-clamp-1 flex items-center gap-1">
          <Video className="w-3 h-3 shrink-0" />
          <span>Video</span>
        </p>
      );
    }

    if (attachmentUrl.startsWith('http') || !!preview.attachment_name) {
      return (
        <p className="text-xs text-muted-foreground line-clamp-1 flex items-center gap-1">
          <FileText className="w-3 h-3 shrink-0" />
          <span>Archivo</span>
        </p>
      );
    }

    if (content) {
      return <p className="text-xs text-muted-foreground line-clamp-1">{content}</p>;
    }

    return <p className="text-xs text-muted-foreground line-clamp-1">No hay mensajes</p>;
  };

  const filteredAnonymousSessions = (anonymousSessions || []).filter((anon) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return 'usuario anónimo'.includes(q) || String(anon.last_message || '').toLowerCase().includes(q);
  });

  const filteredAnonymousConversations = (!session?.user?.id ? anonymousConversations : []).filter((anon) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return String(anon.storeName || 'minibid 24hs').toLowerCase().includes(q)
      || String(anon.lastMessage || '').toLowerCase().includes(q);
  });

  type MergedChatItem =
    | { kind: 'staff-anon'; key: string; sortTs: number; data: any }
    | { kind: 'anon-conv'; key: string; sortTs: number; data: any }
    | { kind: 'dm'; key: string; sortTs: number; data: any }
    | { kind: 'group'; key: string; sortTs: number; data: any };

  const mergedChats = useMemo<MergedChatItem[]>(() => {
    const items: MergedChatItem[] = [];

    // If an anonymous session already has its group-chat representation,
    // avoid showing the legacy "Usuario anónimo" row to prevent duplicates.
    const anonymousSessionIdsFromGroups = new Set(
      (filteredGroups || [])
        .map((group: any) => getAnonymousSessionIdFromGroupDescription(group?.description))
        .filter(Boolean)
    );

    filteredAnonymousSessions.forEach((anon) => {
      if (anonymousSessionIdsFromGroups.has(anon.anonymous_session_id)) {
        return;
      }

      const latestPreview = latestAnonymousPreviewBySessionId[anon.anonymous_session_id];
      const latestAt = latestPreview?.created_at || anon.last_message_at;
      items.push({
        kind: 'staff-anon',
        key: `staff-anon-${anon.anonymous_session_id}`,
        sortTs: latestAt ? new Date(latestAt).getTime() : 0,
        data: anon,
      });
    });

    filteredAnonymousConversations.forEach((anon) => {
      const latestPreview = latestAnonymousPreviewBySessionId[anon.sessionId];
      const latestAt = latestPreview?.created_at || anon.lastMessageAt;
      items.push({
        kind: 'anon-conv',
        key: `anon-conv-${anon.storeId}-${anon.sessionId}`,
        sortTs: latestAt ? new Date(latestAt).getTime() : 0,
        data: anon,
      });
    });

    filteredConversations.forEach((conv) => {
      items.push({
        kind: 'dm',
        key: `dm-${conv.id}`,
        sortTs: conv.last_message_at ? new Date(conv.last_message_at).getTime() : 0,
        data: conv,
      });
    });

    filteredGroups.forEach((group) => {
      const latestPreview = latestGroupPreviewById[group.id];
      const latestAt = latestPreview?.created_at || group.last_message_at;
      items.push({
        kind: 'group',
        key: `group-${group.id}`,
        sortTs: latestAt ? new Date(latestAt).getTime() : 0,
        data: group,
      });
    });

    return items.sort((a, b) => b.sortTs - a.sortTs);
  }, [filteredAnonymousSessions, filteredAnonymousConversations, filteredConversations, filteredGroups, latestAnonymousPreviewBySessionId, latestGroupPreviewById]);

  const handleOpenDM = (conv: typeof conversations[0]) => {
    const title = conv.other_display_name || conv.other_username;
    openChatWindow({
      id: `dm-${conv.id}`,
      type: 'dm',
      conversationId: conv.id,
      otherUserId: conv.other_user_id,
      otherUserSlug: conv.other_slug || undefined,
      title,
      avatarUrl: conv.other_avatar_url,
    });
    closeSidebar();
  };

  const handleOpenGroup = (group: typeof groups[0]) => {
    const supportStoreId = (group as any)?.support_store_id || getStoreIdFromGroupDescription((group as any)?.description);
    const anonymousSessionId = getAnonymousSessionIdFromGroupDescription((group as any)?.description);
    const resolvedAvatarUrl = supportStoreId
      ? (storeAvatarById[supportStoreId] || null)
      : group.avatar_url;
    const displayGroupName = getGroupDisplayName(group);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('chat:group-opened', {
        detail: {
          groupId: group.id,
          anonymousSessionId: anonymousSessionId || undefined,
        },
      }));
    }

    if (anonymousSessionId) {
      openChatWindow({
        id: `staff-anon-${anonymousSessionId}`,
        type: 'dm',
        groupId: group.id,
        title: 'Usuario anónimo',
        avatarUrl: resolvedAvatarUrl,
        isAnonymous: true,
        anonymousSessionId,
        anonymousStoreId: supportStoreId || undefined,
      });
      closeSidebar();
      return;
    }

    openChatWindow({
      id: `group-${group.id}`,
      type: 'group',
      groupId: group.id,
      title: displayGroupName,
      avatarUrl: resolvedAvatarUrl,
    });
    closeSidebar();
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      closeSidebar();
      setIsClosing(false);
    }, 200); // Duración de la animación
  };

  // Activar pull-to-close (solo en móvil)
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: sidebarOpen && !isDesktop
  });

  useScrollLock(sidebarOpen || isClosing, false, scrollAreaRef);

  // Recalcular medidas cuando el sidebar se abre
  useEffect(() => {
    if (sidebarOpen) {
      // Forzar recálculo inmediato
      recalculate();
      // Recalcular también después de múltiples delays para asegurar que el DOM esté actualizado
      const timeoutId1 = setTimeout(recalculate, 0);
      const timeoutId2 = setTimeout(recalculate, 50);
      const timeoutId3 = setTimeout(recalculate, 100);
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
  }, [sidebarOpen, recalculate]);

  // Click fuera para cerrar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sidebarOpen, handleClose]);

  return (
    <>
      {/* Overlay */}
      {(sidebarOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 bg-transparent z-[9997] transition-opacity duration-200',
            isClosing && 'opacity-0 pointer-events-none'
          )}
          onClick={handleClose}
        />
      )}

      <div
      ref={sidebarRef}
      onMouseEnter={() => { if (isDesktop) cancelCloseDelay(); }}
      onMouseLeave={() => { if (isDesktop && sidebarOpen) closeWithDelay(1000); }}
      className={cn(
        'fixed right-0 w-full lg:w-[400px] bg-background z-[9998] flex flex-col transition-transform duration-200',
        offcanvasSidebarPanelClass(sidebarOpen, isClosing)
      )}
      style={{
        top: isDesktop ? `${headerHeight}px` : '0',
        bottom: !isDesktop ? `0` : undefined,
        height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
        pointerEvents: (sidebarOpen && !isClosing) ? 'auto' : 'none'
      }}
    >
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-primary text-white min-h-[64px]">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-white">
          <Mail className="w-5 h-5 text-white" />
          Mensajes
        </h2>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="p-1 hidden md:flex hover:bg-white/10 text-white rounded-lg transition-colors"
                onClick={() => {
                  closeSidebar();
                  navigate('/chat');
                }}
              >
                <Maximize2 className="w-5 h-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Pantalla completa</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsCreateGroupModalOpen(true);
                }}
              >
                <Plus className="w-5 h-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Nuevo grupo</p>
            </TooltipContent>
          </Tooltip>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={(e) => { e.stopPropagation(); handleClose(); }} className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white">
                  <X className="w-5 h-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Cerrar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Search */}
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar conversaciones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full" ref={scrollAreaRef}>
          {loadingConversations ? (
            <div className="p-4 text-center text-muted-foreground">Cargando...</div>
          ) : conversationsError ? (
            <div className="p-4 text-center text-destructive">Error al cargar conversaciones</div>
          ) : mergedChats.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              {searchQuery ? 'No se encontraron chats' : 'No hay chats'}
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {mergedChats.map((item, index, arr) => {
                const prevItem = index > 0 ? arr[index - 1] : null;
                const showSeparator = prevItem && prevItem.kind !== item.kind;
                if (item.kind === 'staff-anon') {
                  const session = item.data;
                  const latestPreview = latestAnonymousPreviewBySessionId[session.anonymous_session_id];
                  const previewAt = latestPreview?.created_at || session.last_message_at;
                  const previewMessage = latestPreview?.message || session.last_message;
                  const supportStoreId = latestPreview?.support_store_id || session.support_store_id || getDefaultSupportStoreId();
                  const staffStoreAvatar = (supportStoreId && storeAvatarById[supportStoreId]) || '/minibid-avatar.png';
                  const staffAnonTitle = 'Minibid · Anónimo';
                  return (
                    <>
                      {showSeparator && (
                        <div className="my-1.5 mx-2 border-t border-border/30" />
                      )}
                      <button
                      key={item.key}
                      onClick={() => {
                        openChatWindow({
                          id: `staff-anon-${session.anonymous_session_id}`,
                          type: 'dm',
                          title: staffAnonTitle,
                          avatarUrl: staffStoreAvatar,
                          isAnonymous: true,
                          anonymousSessionId: session.anonymous_session_id,
                          anonymousStoreId: supportStoreId,
                        });
                        closeSidebar();
                      }}
                      className={cn('w-full p-2.5 transition-all duration-300 text-left flex items-start gap-3 rounded-lg border border-border/20 hover:border-primary/40 bg-transparent hover:bg-primary/5 shadow-none hover:shadow-lg', 'animate-in slide-in-from-bottom duration-200')}
                      style={{ animationDelay: `${index * 20}ms` }}
                    >
                      <div className="relative shrink-0">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={staffStoreAvatar} />
                          <AvatarFallback>AN</AvatarFallback>
                        </Avatar>
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-background rounded-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="font-semibold text-sm">{staffAnonTitle}</span>
                          {previewAt && (
                            <span className="text-xs text-muted-foreground shrink-0">
                              {format(new Date(previewAt), 'HH:mm')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground line-clamp-1 flex-1">{previewMessage}</p>
                          {((session.unread_count || 0) > 0 || session.has_unread) && (
                            <Badge className="ml-1 shrink-0 bg-red-500 text-white text-xs px-1.5 h-5 min-w-5 flex items-center justify-center">
                              {(session.unread_count || 0) > 0 ? session.unread_count : '•'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </button>
                    </>
                  );
                }

                if (item.kind === 'anon-conv') {
                  const anonConv = item.data;
                  const baseStoreName = anonConv.storeName || 'Minibid 24hs';
                  const storeName = `${baseStoreName} · Anónimo`;
                  const storeAvatar = (anonConv.storeId ? storeAvatarById[anonConv.storeId] : null) || anonConv.storeAvatarUrl || '/minibid-avatar.png';
                  const latestPreview = latestAnonymousPreviewBySessionId[anonConv.sessionId];
                  const previewAt = latestPreview?.created_at || anonConv.lastMessageAt;
                  const previewMessage = latestPreview?.message || anonConv.lastMessage;

                  return (
                    <>
                      {showSeparator && (
                        <div className="my-1.5 mx-2 border-t border-border/30" />
                      )}
                      <button
                      key={item.key}
                      onClick={() => {
                        openChatWindow({
                          id: `help-anonymous-${anonConv.storeId}-${anonConv.sessionId}`,
                          type: 'dm',
                          title: storeName,
                          avatarUrl: storeAvatar,
                          isAnonymous: true,
                          anonymousSessionId: anonConv.sessionId,
                          anonymousStoreId: anonConv.storeId,
                        });
                        closeSidebar();
                      }}
                      className={cn('w-full p-2.5 transition-all duration-300 text-left animate-in slide-in-from-bottom duration-200 rounded-lg border border-border/20 hover:border-primary/40 bg-transparent hover:bg-primary/5 shadow-none hover:shadow-lg')}
                      style={{ animationDelay: `${index * 20}ms` }}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className="relative shrink-0">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={storeAvatar || undefined} />
                            <AvatarFallback>M</AvatarFallback>
                          </Avatar>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <span className="font-medium text-sm line-clamp-1 flex-1">{storeName}</span>
                            {previewAt && (
                              <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                                {formatDistanceToNow(new Date(previewAt), { locale: es, addSuffix: true })}
                              </span>
                            )}
                          </div>
                          <div className="line-clamp-1 text-xs text-muted-foreground">{previewMessage}</div>
                        </div>
                        {anonConv.unreadCount > 0 && (
                          <Badge className="ml-1 shrink-0 bg-red-500 hover:bg-red-600 text-white text-xs px-1.5 h-5 min-w-5 flex items-center justify-center">{anonConv.unreadCount}</Badge>
                        )}
                      </div>
                    </button>
                    </>
                  );
                }

                if (item.kind === 'dm') {
                  const conv = item.data;
                  const dmPreview = latestDmPreviewById[conv.id] || {
                    content: conv.last_message_content || null,
                    attachment_url: null,
                    attachment_type: null,
                    attachment_name: null,
                  };
                  return (
                    <>
                      {showSeparator && (
                        <div className="my-1.5 mx-2 border-t border-border/30" />
                      )}
                      <button
                      key={item.key}
                      onClick={() => handleOpenDM(conv)}
                      className={cn('w-full p-2.5 transition-all duration-300 text-left animate-in slide-in-from-bottom duration-200 rounded-lg border border-border/20 hover:border-primary/40 bg-transparent hover:bg-primary/5 shadow-none hover:shadow-lg')}
                      style={{ animationDelay: `${index * 20}ms` }}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className="relative shrink-0">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={conv.other_avatar_url || undefined} />
                            <AvatarFallback>{getAvatarInitials(conv.other_display_name || conv.other_username || '')}</AvatarFallback>
                          </Avatar>
                          {isOnline(conv.other_user_id) && (
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-background" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <span className="font-medium text-sm line-clamp-1 flex-1">{conv.other_display_name || conv.other_username}</span>
                            {conv.last_message_at && (
                              <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                                {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: false, locale: es })}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">{renderMessagePreview(dmPreview)}</div>
                            {conv.unread_count > 0 && (
                              <Badge className="shrink-0 h-5 min-w-5 px-1.5 text-xs bg-red-500 hover:bg-red-600 text-white">{conv.unread_count}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                    </>
                  );
                }

                const group = item.data;
                const supportStoreId = group?.support_store_id || getStoreIdFromGroupDescription(group?.description);
                const anonymousSessionId = getAnonymousSessionIdFromGroupDescription(group?.description);
                const groupAvatarUrl = supportStoreId
                  ? (storeAvatarById[supportStoreId] || null)
                  : group.avatar_url;
                          const starterProfile = groupStarterByGroupId[group.id];
                const displayGroupName = getGroupDisplayName(group);
                const groupPreview = latestGroupPreviewById[group.id] || {
                  content: group.last_message_content || null,
                  attachment_url: null,
                  attachment_type: null,
                  attachment_name: null,
                  created_at: group.last_message_at || null,
                };
                return (
                  <>
                    {showSeparator && (
                      <div className="my-1.5 mx-2 border-t border-border/30" />
                    )}
                    <button
                    key={item.key}
                    onClick={() => handleOpenGroup(group)}
                    className={cn('w-full p-2.5 transition-all duration-300 text-left animate-in slide-in-from-bottom duration-200 rounded-lg border border-border/20 hover:border-primary/40 bg-transparent hover:bg-primary/5 shadow-none hover:shadow-lg')}
                    style={{ animationDelay: `${index * 20}ms` }}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="relative shrink-0">
                        <Avatar className="w-12 h-12 rounded-lg">
                          <AvatarImage src={groupAvatarUrl || undefined} />
                          <AvatarFallback className="rounded-lg">
                            {groupAvatarUrl ? <Users className="w-6 h-6" /> : getAvatarInitials(group.name)}
                          </AvatarFallback>
                        </Avatar>
                        {starterProfile && (
                          <div className="absolute -bottom-1 -right-1">
                            <Avatar className="w-6 h-6 rounded-full border-2 border-background">
                              <AvatarImage src={starterProfile.avatarUrl || undefined} />
                              <AvatarFallback className="text-[10px]">
                                {getAvatarInitials(starterProfile.displayName)}
                              </AvatarFallback>
                            </Avatar>
                            <div
                              className={cn(
                                'absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 rounded-full border border-background',
                                isOnline(starterProfile.userId) ? 'bg-green-500' : 'bg-muted-foreground/60'
                              )}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <span className="font-medium text-sm line-clamp-1 flex-1">{displayGroupName}</span>
                          {group.last_message_at && (
                            <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                              {formatDistanceToNow(new Date(group.last_message_at), { addSuffix: false, locale: es })}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 min-w-0">
                          <div className="flex-1 min-w-0">{renderMessagePreview(groupPreview)}</div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-xs text-muted-foreground hidden sm:inline">{group.member_count} miembros</span>
                            {group.unread_count > 0 && (
                              <Badge className="h-5 min-w-5 px-1.5 text-xs bg-red-500 hover:bg-red-600 text-white">{group.unread_count}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                  </>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Create Group Modal */}
      <CreateGroupChatModal
        isOpen={isCreateGroupModalOpen}
        onClose={() => setIsCreateGroupModalOpen(false)}
        onGroupCreated={(groupId, groupName, avatarUrl) => {
          setIsCreateGroupModalOpen(false);
          // Close sidebar
          handleClose();
          // Open the new group chat window
          openChatWindow({
            id: `group-${groupId}`,
            type: 'group',
            groupId: groupId,
            title: groupName,
            avatarUrl: avatarUrl || null,
          });
        }}
      />
    </div>
    </>
  );
};
